<template>
  <RequireAdContract>
    <WithCampaignInternalNav>
      <Content
        class="campaign-view"
        :title="$t('ADVERTISEMENT.CAMPAIGN_DETAILS.TITLE')"
      >
        <div class="campaign-view__info">
          <span>{{ $t('ADVERTISEMENT.CAMPAIGN_VIEW.CAMPAIGN_NAME') }}:</span>
          <span>
            {{ campaignInfo.name }}
          </span>
        </div>
        <div class="campaign-view__info">
          <span>{{ $t('ADVERTISEMENT.CAMPAIGN_VIEW.STATUS') }}:</span>
          <span :class="campaignStatusClass">
            {{ $t(translateCampaignStatus(campaignInfo.status)) }}
          </span>
        </div>

        <CampaignViewActions
          :paused="campaignInfo.paused"
          :name="campaignInfo.name"
          @changeStatus="changeCampaignStatus"
        />

        <CampaignViewHints class="campaign-view__hints" />

        <hr class="divider" />

        <Transition name="fade-transform" mode="out-in">
          <RouterView />
        </Transition>
      </Content>
    </WithCampaignInternalNav>
  </RequireAdContract>
</template>

<script>
import RequireAdContract from '../../common/RequireAdContract';
import layout from '@/mixins/layout';
import permissions from '@/mixins/permissions';

import { SearchAdsCampaigns, Apps } from '@/api/api';

import campaignId from '@/mixins/advertisement/campaignId';
import lang from '@/mixins/lang';
import {
  SEARCH_ADS_CAMPAIGN_HINTS_SET_BUDGET_HINT,
  SEARCH_ADS_CAMPAIGN_HINTS_SET_KEYWORDS_HINT,
  SEARCH_ADS_CAMPAIGN_HINTS_SET_CPI_LIMIT,
  SEARCH_ADS_RESET_HINTS,
  CAMPAIGN_BIDS,
} from '@/store/types';
import Content from '@@/common/layout/Content';
import CampaignViewActions from './CampaignViewActions';

import WithCampaignInternalNav from './WithCampaignInternalNav';
import CampaignViewHints from './CampaignViewHints';

import translateCampaignStatus from '../../common/utility/translators/translateCampaignStatus';
import { mapActions } from 'vuex';
import {
  SEARCH_ADS_APP_CPI_LIMIT,
  SEARCH_ADS_GAME_CPI_LIMIT,
} from '../Bids/BidsContent/constants';

export default {
  name: 'CampaignView',
  components: {
    WithCampaignInternalNav,
    Content,
    CampaignViewActions,
    CampaignViewHints,
    RequireAdContract,
  },

  mixins: [layout, permissions, campaignId, lang],

  data() {
    return {
      isLoading: true,

      campaignInfo: {
        name: '',
        status: '',
        paused: false,
      },
    };
  },

  computed: {
    campaignStatusClass() {
      const { status } = this.campaignInfo;
      return {
        'text-success': status === 'active',
        'text-alert': status === 'paused',
        'text-warning': status !== 'active' && status !== 'paused',
      };
    },
  },

  created() {
    this.setPageTitle('ADVERTISEMENT.TITLE');
    this.getCampaignData();
  },

  methods: {
    ...mapActions({
      setKeywordHint: SEARCH_ADS_CAMPAIGN_HINTS_SET_KEYWORDS_HINT,
      setBudgetHint: SEARCH_ADS_CAMPAIGN_HINTS_SET_BUDGET_HINT,
      setCpiLimit: SEARCH_ADS_CAMPAIGN_HINTS_SET_CPI_LIMIT,
      resetHints: SEARCH_ADS_RESET_HINTS,
    }),
    translateCampaignStatus,
    getCampaignData() {
      this.isLoading = true;

      return Promise.all([this.getCampaignInfo(), this.getCampaignBids()])
        .catch(({ message }) => {
          if (message) {
            this.$message.error(message);
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    getCampaignInfo() {
      return SearchAdsCampaigns.getCampaignInfo(this.campaignId).then(
        ({ name, status, paused, budget, hasBid, packageName }) => {
          this.setKeywordHint(!hasBid);
          this.setBudgetHint(!budget);
          this.checkAppTypeAndCpiLimit(packageName);
          this.campaignInfo = {
            name,
            status,
            paused,
          };
        },
      );
    },
    checkAppTypeAndCpiLimit(packageName) {
      // TODO: this logic is not good, should be improved
      Apps.get(packageName)
        .then(app => {
          const isGame = app?.category.game;
          this.setCpiLimit(
            isGame ? SEARCH_ADS_GAME_CPI_LIMIT : SEARCH_ADS_APP_CPI_LIMIT,
          );
        })
        .catch(() => {
          this.setCpiLimit(SEARCH_ADS_APP_CPI_LIMIT);
        });
    },
    getCampaignBids() {
      return this.$store.dispatch(CAMPAIGN_BIDS, {
        campaignId: this.campaignId,
        range: 'last-week',
      });
    },
    changeCampaignStatus() {
      this.campaignInfo.paused = !this.campaignInfo.paused;

      this.getCampaignInfo();
    },
  },
  beforeRouteLeave(to, from, next) {
    this.resetHints();
    next();
  },
};
</script>

<style lang="scss" scoped>
.campaign-view {
  &__info {
    margin-bottom: $global-margin / 2;
  }

  &__hints {
    margin-top: $global-margin;
  }
}
</style>
